import { BuildingDTO } from "./building";
import { Conversation } from "./conversation";
import { InsuranceIssue } from "./insurance";
import { RentalContract } from "./rental-contracts";
import { Resource } from "./resource";
import { UnitDTO } from "./unit";

export enum TicketPriority {
    UNKNOWN = 'Unknown',
    LOW = 'Laag',
    MEDIUM = 'Gemiddeld',
    HIGH = 'Hoog',
}

export enum TicketInternalStatus {
    UNKNOWN = 'Unknown',
    NEW_UNANSWERED = 'Nieuwe tickets (Onbeantwoord)',
    CONFIRMATION_SENT = 'Bevestiging van ontvangst verstuurd',
    DAILY_MEETING_TOPICS = 'Speerpunten dagelijkse helpdeskmeeting',
    ADDITIONAL_INFO_NEEDED = 'Bijkomende info noodzakelijk na dagelijkse helpdeskmeeting',
    SUPPLIER_TO_CONTACT = 'Leverancier te contacteren',
    SUPPLIER_CONTACTED = 'Leverancier gecontacteerd (voor data)',
    /**
     * Bewust geparkeerd: er is nu niets aan te doen (huurder onbereikbaar,
     * wachten op een seizoen, een levering die maanden duurt). Het ticket blijft
     * **open** — uitgesteld is niet afgehandeld — maar staat uit de dagelijkse
     * stroom.
     */
    SNOOZE = 'Snooze',
    EXECUTION_DATE_KNOWN = 'Datum uitvoering gekend en huurder(s) verwittigd',
    WORK_COMPLETED = 'Werken zijn uitgevoerd',
    COMPLETED = 'Voltooid'
}

export enum TicketLabel {
    Afbraakwerken = 'Afbraakwerken',
    Asbest = 'Asbest',
    Balustrade = 'Balustrade',
    Binnenschrijnwerk = 'Binnenschrijnwerk',
    Brandbeveiliging = 'Brandbeveiliging',
    Dak = 'Dak',
    ElastischeVoegen = 'Elastische Voegen',
    ElectronischeToestellen = 'Electronische toestellen',
    Elektriciteit = 'Elektriciteit',
    Nutsvoorzieningen = 'Nutsvoorzieningen',
    Boekhouding = 'Boekhouding',
    Facturatie = 'Facturatie',
    Garage = 'Garage',
    Gebouwattributen = 'Gebouwattributen',
    Gevel = 'Gevel',
    HVAC = 'HVAC',
    Huurdersdiscussies = 'Huurdersdiscussies',
    Inrichting = 'Inrichting',
    Internet = 'Internet',
    Lift = 'Lift',
    Onderhoud = 'Onderhoud',
    AfvalEnOngedierte = 'Afval & Ongedierte',
    Karweien = 'Karweien',
    Poetsen = 'Poetsen',
    Overige = 'Overige',
    Toegangsbeheer = 'Sloten- en toegangsbeheer',
    Buitenschrijnwerk = 'Buitenschrijnwerk',
    Riolering = 'Riolering',
    Rolluik = 'Rolluik',
    Ruwbouw = 'Ruwbouw',
    Verstopping = 'Verstopping',
    Sanitair = 'Sanitair',
    Stabiliteit = 'Stabiliteit',
    SuggestiesHuurdersVerbeteringen = 'Suggesties huurders ikv verbeteringen',
    Trap = 'Trap',
    Ventilatie = 'Ventilatie',
    VergunningAttest = 'Vergunning/attest',
    Verlichting = 'Verlichting',
    Verwarming = 'Verwarming',
    VloerenParket = 'Vloeren en parket',
    VochtWater = 'Vocht/water',
    VragenHuurcontract = 'Vragen mbt huurcontract',
    UNKNOWN = 'Onbekend',
    Tuinonderhoud = 'Tuinonderhoud',
    Signalisatie = 'Signalisatie',
    Meubilair = 'Meubilair',
    Glasbreuk = 'Glasbreuk',
    Bezetting = 'Bezetting',
  }

  export enum  TicketSource {
    UNKNOWN = 'Onbekend',
    INTERNAL = 'Intern',
    TENANT = 'Huurder',
    NEIGHBOUR = 'Buur',
  }

  
export enum TicketAccountingType {
  UNKNOWN = 'Onbekend',
  OPEX = 'OPEX',
  CAPEX = 'CAPEX',
}


/** Eén leverancier-toewijzing op een helpdeskticket (koppeltabel). */
export interface TicketSupplierAssignment {
    id: number;
    supplierId: number;
    sortOrder: number;
    supplier: {
        id: number;
        name: string;
        email?: string | null;
        phone?: string | null;
    };
    /**
     * De gekozen contactpersoon bij deze leverancier. Staat die er, dan gaat een
     * mail over dit ticket naar díe persoon i.p.v. naar het algemene adres van de
     * firma. `null`/afwezig betekent "naar de firma".
     */
    contactId?: number | null;
    contact?: {
        id: number;
        firstName: string;
        lastName: string;
        email?: string | null;
        jobTitle?: string | null;
        phone_mobile?: string | null;
    } | null;
}

export interface HelpdeskTicketInterface {
    id?: number;
    title: string;
    building?: BuildingDTO;
    buildingId?: string | null;
    unit?: UnitDTO | null;
    unitId?: string | null;
    rentalContract: RentalContract | null;
    assignedTo: string | null; // Clerk user id
    priority: TicketPriority;
    odooId: number | null;
    internalStatus: TicketInternalStatus;
    internalAnalysis: string | null;
    label: TicketLabel;
    description: string | null;
    insuranceIssue: InsuranceIssue | null;
    conversations: Conversation[];
    accountingType: TicketAccountingType;
    /**
     * @deprecated Een ticket heeft sinds de multi-leverancier refactor meerdere
     * leveranciers via `supplierAssignments`. Lees niet meer rechtstreeks van
     * `supplier`; gebruik de helpers in `screens/helpdesk/shared/ticketSuppliers.ts`.
     * Blijft optioneel staan voor legacy-code.
     */
    supplier?: Resource | null;
    /** Bron van waarheid: meerdere leveranciers per ticket (koppeltabel ticket_supplier_assignment). */
    supplierAssignments?: TicketSupplierAssignment[];
    reporter: Resource | null;
    source: TicketSource;
    materialsToOrder: string | null;
    materialsToBring: string | null;
    solutionProposal: string | null;
    actionsTaken: string | null;
    estimatedCost: number | null;
    /**
     * Som van alle gekoppelde OPEX-factuurregels (excl. btw). Backend-berekend
     * uit `invoice_analytic_line`; dit is de werkelijke factuurkost en staat los
     * van `estimatedCost` (de vooraf geschatte kost).
     */
    actualCost?: number | null;
    /** Unieke factuurnummers van de gekoppelde factuurregels. */
    invoiceNumbers?: string[];
    /** Aantal gekoppelde factuurregels (kan hoger zijn dan het aantal facturen). */
    invoiceLineCount?: number;
    createdAt?: Date;
    updatedAt?: Date;
    completedAt?: Date | null;
    reopenedAt?: Date | null;
    // Email-related properties
    emailCount?: number;
    hasAttachments?: boolean;
    hasRecentEmails?: boolean;
    hasUnreadEmails?: boolean;
    unreadEmailCount?: number;
    // Phase duration
    daysInCurrentPhase?: number;
    // Renovation project link
    renovationProject?: { id: number; title: string; status?: string } | null;
    renovationProjectId?: number | null;
}